/**
 * "Copy path ▸ abfss:// URI · DFS URL · Blob URL · Path" of a Data Lake row (S8 Task 8.10). Every string comes from
 * the Task 8.2 builders over the connection's ENDPOINTS (never its credential), so no query — and no `sig=` — can reach
 * the clipboard; `undefined` when the connection has no endpoint for that format (e.g. an emulator has no dfs host).
 */
import type { Connection } from '../../../../types/electron';
import type { HnsPathRef } from '../../../../types/azureStorage';
import { abfssUri, blobViewOfHnsPath, dfsPathUrl } from '../../../../core/engines/azureStorageHnsPaths';
import { normalizeStorageAccountInput, serviceEndpointFor } from '../../../../core/engines/azureStorageUrl';
import { storageResourceUrl } from '../utils/storageUrls';

export type AdlsPathFormat = 'abfss' | 'dfs' | 'blob' | 'path';
export const ADLS_PATH_FORMATS: ReadonlyArray<{ format: AdlsPathFormat; label: string }> = [
  { format: 'abfss', label: 'abfss:// URI' },
  { format: 'dfs', label: 'DFS URL' },
  { format: 'blob', label: 'Blob URL' },
  { format: 'path', label: 'Path' },
];

type EndpointConnection = Pick<Connection, 'server' | 'azureStorageEndpoints' | 'azureStorageEmulator'>;

function dfsEndpointOf(connection: EndpointConnection): { accountName?: string; dfsEndpoint?: string } {
  const accountName = normalizeStorageAccountInput(connection.server ?? '').accountName;
  const dfsEndpoint = serviceEndpointFor({ accountName, endpoints: connection.azureStorageEndpoints, emulator: connection.azureStorageEmulator }, 'dfs');
  return { accountName, dfsEndpoint };
}

export function adlsPathText(connection: EndpointConnection, ref: HnsPathRef, format: AdlsPathFormat): string | undefined {
  switch (format) {
    case 'path':
      return ref.path ? `${ref.fileSystem}/${ref.path}` : ref.fileSystem;
    case 'blob':
      return storageResourceUrl(connection, blobViewOfHnsPath(ref));
    case 'dfs': {
      const { dfsEndpoint } = dfsEndpointOf(connection);
      return dfsEndpoint ? dfsPathUrl({ dfsEndpoint }, ref) : undefined;
    }
    case 'abfss': {
      const { accountName, dfsEndpoint } = dfsEndpointOf(connection);
      return dfsEndpoint ? abfssUri({ accountName: accountName ?? '', dfsEndpoint }, ref) : undefined;
    }
  }
}
